import { FCC } from "@/types/common";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { Grid, Stack, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetTalentSchema } from "@/features/talent/hooks/useGetTalentSchema";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { dayjs } from "@/utils/dayjs";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { useGetNationalityList, useGetResidenceStatusList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { Form } from "@/components/elements/Form";
import { CityInput } from "@/components/elements/CityInput";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { ERecruitmentStatus, TALENT_LOCATION } from "@/types/enum";
import { YearMonthAmountInput } from "@/features/talent/components/talent-form/YearMonthAmountInput";
import { calculateSpecificSkillTransitionPeriod } from "@/features/talent/utils/calculateSpecificSkillTransitionPeriod";
import { useMe } from "@/api/services/main/auth";

export type TCreateTalentFromCardTabForm = {
  defaultValues?: TTalentForm | null;
  onChange: (data: TTalentForm) => void;
  memo?: string | null;
  onValidate: (triggerFn: () => Promise<boolean>) => void;
};

/**
 * Form for the 在留カード項目 tab of the unified scan modal (IROHANA_MATCH_DEV-280).
 *
 * Residence-card-only subset per tab3.png mockup. Sections:
 *   1. 在留カード情報    — fullName, birthday, nationality, gender
 *   2. 現住所（国内）    — zipCode, prefecture, city, address
 *   3. 在留資格情報      — residenceStatus, periodOfStay, SSW1 startDate,
 *                          blankPeriodMonthly, SSW1 remainingTerm (computed)
 *
 * Excluded vs the 全項目 / 履歴書項目 forms: contact info (phone, email),
 * 本国における居住地, 分野・日本語能力, 学歴・保有資格, 職歴, 家族構成,
 * 特性メモ, 面談評価, contactMethods, 未入国 checkbox, passport.
 *
 * SSW1 残存期間 is read-only and computed locally via
 * `calculateSpecificSkillTransitionPeriod` (same logic as TalentFormResidenceStatus,
 * inlined here so the simplified card tab does not pull in the bordered
 * collapsible component which carries extra fields not in the mockup).
 */
export const CreateTalentFromCardTabForm: FCC<TCreateTalentFromCardTabForm> = ({ defaultValues, onChange, onValidate }) => {
  const { talentSchema } = useGetTalentSchema();
  const { nationalityOptions } = useGetNationalityList();
  const { residenceStatusOptions } = useGetResidenceStatusList();
  const { data: genderOptions } = useEnum("EGender");
  const { data: user } = useMe();
  const validationContextRef = useRef({ isFormSubmitted: false });

  const methods = useForm<TTalentForm>({
    defaultValues: { ...defaultValues },
    resolver: yupResolver(talentSchema),
    context: validationContextRef.current,
    mode: "all",
    reValidateMode: "onBlur",
  });
  const {
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors, isSubmitted, touchedFields },
  } = methods;

  const nickName = useWatch({ control, name: "nickName" });
  const specifiedSkillFirstStartDate = useWatch({ control, name: "specifiedSkillFirstStartDate" });
  const blankPeriodMonthly = useWatch({ control, name: "blankPeriodMonthly" });

  const locationInfo = useLocationInfo({
    methods,
    names: { zipCode: "zipCode", province: "prefecture", municipality: "city", address: "address" },
  });

  // Initial wiring: draft status + owner. No memo prefill (resume-only fields don't apply here).
  // Also: BE returns blankPeriodMonthly=0 when work_experiences exist but the
  // computed gap is 0 months. UI convention is to render empty for 0 (so the
  // input doesn't sit at "0" by default, which looks like a stale prefilled
  // value). Reset 0 → null so the Input renders empty and the user gets a
  // clean slate.
  useEffect(() => {
    setValue("recruitmentStatus", ERecruitmentStatus.DRAFT);
    setValue("personInChargeId", Number(user?.data.id));
    if (Number(getValues("blankPeriodMonthly")) === 0) {
      setValue("blankPeriodMonthly", null);
    }
  }, []);

  // Expose validate trigger to parent.
  useEffect(() => {
    onValidate(async () => {
      validationContextRef.current.isFormSubmitted = true;
      let isValid = false;
      await methods.handleSubmit(
        () => {
          isValid = true;
        },
        () => {
          isValid = false;
        },
      )();
      return isValid;
    });
  }, [methods, errors, onValidate]);

  // Re-validate fullName when nickName changes (draft allows fullName empty when nickName provided).
  useEffect(() => {
    if (isSubmitted || touchedFields.fullName || touchedFields.nickName) {
      trigger("fullName");
    }
  }, [nickName, trigger, isSubmitted, touchedFields.fullName, touchedFields.nickName]);

  // Recompute SSW1 remaining term whenever start date or blank period changes.
  // Mirrors TalentFormResidenceStatus logic; inlined to avoid pulling that
  // component (which adds bordered card + 未入国 checkbox + passport fields
  // that are not in the card-tab mockup).
  useEffect(() => {
    if (!specifiedSkillFirstStartDate) {
      setValue("specifiedSkillFirstRemainingTerm", "");
      setValue("specifiedSkillFirstRemainingTermYear", 0);
      setValue("specifiedSkillFirstRemainingTermMonth", 0);
      return;
    }
    const { yearPeriod, monthPeriod } = calculateSpecificSkillTransitionPeriod({
      specifiedSkillFirstStartDate,
      blankPeriodMonthly: blankPeriodMonthly?.toString() || "0",
    });
    setValue("specifiedSkillFirstRemainingTerm", `${yearPeriod}/${monthPeriod}`);
    setValue("specifiedSkillFirstRemainingTermYear", yearPeriod);
    setValue("specifiedSkillFirstRemainingTermMonth", monthPeriod);

    // Re-run schema test so the FormField below renders the error message
    // (e.g. ">5年0か月" / "<0年0か月"). Without this, the computed value updates
    // but fieldState.error stays stale → silent error.
    trigger("specifiedSkillFirstRemainingTerm");
  }, [specifiedSkillFirstStartDate, blankPeriodMonthly, setValue, trigger]);

  // Mirror form state to parent.
  useEffect(() => {
    const subscription = watch(() => onChange(getValues()));
    return () => subscription.unsubscribe();
  }, [watch, onChange, getValues]);

  const isReadOnly = watch("location") === TALENT_LOCATION.OVERSEA;
  const onSubmit = () => {};

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={talentSchema} shouldBlock={false} enableBlocker={false}>
      {/* 1. 在留カード情報 */}
      <Typography variant="sub16Bold">在留カード情報</Typography>
      <Grid container spacing={3} mt={0}>
        <Grid item xs={6}>
          <FormField control={control} name="fullName" required={!nickName?.trim()} sx={{ marginBottom: "24px" }} currentValueType="text">
            <Input />
          </FormField>
          <FormField control={control} name="nationalityId" currentValueType="select" currentOptionsKey="nationality">
            <Select placeholder="選択してください" options={nationalityOptions} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="birthday" sx={{ marginBottom: "24px" }} currentValueType="date">
            <InputDatePicker minDate={dayjs("1870/01/01")} maxDate={dayjs().subtract(18, "year")} />
          </FormField>
          <FormField control={control} name="gender" currentValueType="select" currentOptionsKey="gender">
            <RadioGroup direction="row" options={genderOptions} />
          </FormField>
        </Grid>
      </Grid>

      {/* 2. 現住所（国内） */}
      <Stack mt={3}>
        <Typography variant="sub16Bold">現住所（国内）</Typography>
      </Stack>
      <Grid container spacing={3} mb={3} mt={0}>
        <Grid item xs={12}>
          <FormField control={control} name="zipCode" currentValueType="text">
            <Input placeholder="入力してください" disabled={isReadOnly} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="prefecture" currentValueType="text">
            <Input placeholder="入力してください" readonly />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <CityInput name="city" control={control} zipCodeFieldName="zipCode" locationInfo={locationInfo} disabled={isReadOnly} />
        </Grid>
        <Grid item xs={12}>
          <FormField control={control} name="address" currentValueType="text">
            <Input placeholder="入力してください" disabled={isReadOnly} />
          </FormField>
        </Grid>
      </Grid>

      {/* 3. 在留資格情報 — inline (no bordered card, no 未入国 checkbox, no passport) */}
      <Typography variant="sub16Bold">在留資格情報</Typography>
      <Grid container spacing={3} mt={0}>
        <Grid item xs={6}>
          <FormField control={control} name="residenceStatusId" currentValueType="select" currentOptionsKey="residenceStatus">
            <Select placeholder="選択してください" options={residenceStatusOptions} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="periodOfStay" currentValueType="date">
            <InputDatePicker />
          </FormField>
        </Grid>
      </Grid>
      <Grid container spacing={3} mb={3} mt={0}>
        <Grid item xs={4}>
          <FormField control={control} name="specifiedSkillFirstStartDate" currentValueType="date">
            <InputDatePicker />
          </FormField>
        </Grid>
        <Grid item xs={4}>
          <FormField control={control} name="blankPeriodMonthly" currentValueType="text">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={4}>
          <FormField control={control} name="specifiedSkillFirstRemainingTerm" currentValueType="text">
            <YearMonthAmountInput
              readonly={true}
              isError={false}
              nameYear="specifiedSkillFirstRemainingTermYear"
              nameMonth="specifiedSkillFirstRemainingTermMonth"
            />
          </FormField>
        </Grid>
      </Grid>
    </Form>
  );
};
